import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { MdClose } from 'react-icons/md';
import './ConfirmModal.css';

export default function ConfirmModal({
  isOpen,
  title = 'Confirm',
  message = 'Are you sure you want to proceed?',
  confirmText = 'OK',
  cancelText = 'Cancel',
  icon: Icon = null,
  danger = false,
  onConfirm,
  onClose,
}) {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isExiting, setIsExiting] = useState(false);
  const closingRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsExiting(false);
      closingRef.current = false;
    } else if (shouldRender && !closingRef.current) {
      closingRef.current = true;
      setIsExiting(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsExiting(false);
        closingRef.current = false;
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const initiateClose = (actionCallback) => {
    if (closingRef.current) return;
    closingRef.current = true;
    setIsExiting(true);
    setTimeout(() => {
      if (actionCallback) actionCallback();
      if (onClose) onClose();
      setShouldRender(false);
      setIsExiting(false);
      closingRef.current = false;
    }, 200);
  };

  const handleClose = () => {
    initiateClose(null);
  };

  const handleConfirm = () => {
    initiateClose(onConfirm);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && shouldRender && !closingRef.current) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shouldRender]);

  if (!shouldRender) return null;

  return ReactDOM.createPortal(
    <div
      className={`yt-confirm-modal-backdrop ${isExiting ? 'exiting' : ''}`}
      onClick={handleBackdropClick}
    >
      <div className={`yt-confirm-modal-card ${isExiting ? 'exiting' : ''} ${danger ? 'danger-modal' : ''}`}>
        <div className="yt-confirm-modal-header">
          {Icon && (
            <div className={`yt-confirm-modal-icon ${danger ? 'danger-icon' : ''}`}>
              <Icon size={24} />
            </div>
          )}
          <h3 className="yt-confirm-modal-title">{title}</h3>
          <button className="yt-confirm-modal-close-btn" onClick={handleClose} title="Close">
            <MdClose size={20} />
          </button>
        </div>

        <div className="yt-confirm-modal-body">
          <p>{message}</p>
        </div>

        <div className="yt-confirm-modal-actions">
          <button className="yt-confirm-btn yt-confirm-btn-cancel" onClick={handleClose}>
            {cancelText}
          </button>
          <button
            className={`yt-confirm-btn ${danger ? 'yt-confirm-btn-danger' : 'yt-confirm-btn-primary'}`}
            onClick={handleConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
